import React, { FC, useEffect, useMemo, useState } from 'react'
import { useLazyGetAndTryDecryptMainAttachmentQuery } from '../../../../../../core/api/documentApi'
import { DecryptedService, TelecomType } from '@icure/cardinal-sdk'
import { Button, message, notification, Tooltip } from 'antd'
import { CloudUploadOutlined, DownloadOutlined, MailOutlined } from '@ant-design/icons'
import { getPdfUrl } from '../../../../../../helpers/fileToBase64'
import './index.less'
import { computeDetection, DetectionTag, EyeVals } from '../helpers'
import { DecryptedContact, DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { getTelecomBySystem } from '../../../../../../helpers/patientDataManipulations'
import { useSendEmailMutation } from '../../../../../../core/api/msgGwApi'
import { int8ArrayToBase64 } from '../../../../../../helpers/hash'
import { emailHtml, emailSubject, getPropertyStringValue } from './helpers'
import { useTranslation } from 'react-i18next'
import { BeCardinalProvider } from '@icure/be-hubs-access'
import { CardinalApiState, setEhealthPassphrase } from '../../../../../../core/services/auth.api'
import { ModalUploadReportToHub } from '../../../../PatientsDirectory/modals/ModalUploadReportToHub'
import { useGetPractitionerQuery } from '../../../../../../core/api/practitionerApi'
import { createSelector } from '@reduxjs/toolkit'
import { useAppDispatch, useAppSelector } from '../../../../../../core/hooks'
import { skipToken } from '@reduxjs/toolkit/query'
import { HubEnum, hubsTitles, PRACTITIONER_FHC_URL_IDENTIFIER, PRACTITIONER_HUB_ID_IDENTIFIER } from '../../../../../../constants'
import { SupportedLang } from '../../../../../../i18n'
import { extractQualityFromPractitioner } from '../../../../../../helpers/qualityTags'
import { SpinLoader } from '../../../../../common/SpinLoader'

interface ConsultationHeaderProps {
  diabeticMaculopathyExaminationValue: { dexter: EyeVals; sinister: EyeVals }
  diabeticRetinopathyExaminationValue: { dexter: EyeVals; sinister: EyeVals }
  dateOfExamination?: string
  pdfReportService?: DecryptedService
  handleOnMouseEnter: () => void
  handleOnMouseLeave: () => void
  patient: DecryptedPatient
  contact: DecryptedContact
}

const SEND_REPORT_TEMPLATE_ID = '7faf896f-4b53-4621-97e2-07c10616ba67'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
    ehealthPassphrase: cardinalApi.ehealthPassphrase,
  }),
)

export const ConsultationHeader: FC<ConsultationHeaderProps> = ({
  diabeticMaculopathyExaminationValue,
  diabeticRetinopathyExaminationValue,
  dateOfExamination,
  pdfReportService,
  handleOnMouseEnter,
  handleOnMouseLeave,
  patient,
  contact,
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const pdfReportDocumentId = pdfReportService?.content?.documentId?.documentId
  const { healthcarePartyId, ehealthPassphrase } = useAppSelector(reduxSelector)

  // LAZY query instead of immediate query
  const [fetchPdfReport, { data: decryptedPdfReportDocument, isFetching }] = useLazyGetAndTryDecryptMainAttachmentQuery()
  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(healthcarePartyId ?? skipToken)

  const [sendEmail, { isLoading: isEmailSending, error: sendEmailError, isError: isEmailSendingHasError, isSuccess: isEmailSentSuccessfully }] = useSendEmailMutation()
  const [messageApi, messageCtx] = message.useMessage()
  const [notificationApi, notificationCtx] = notification.useNotification()

  const [isLoadingPdf, setIsLoadingPdf] = useState(false)
  const [shouldAutoDownload, setShouldAutoDownload] = useState(false)
  const [shouldAutoSendEmail, setShouldAutoSendEmail] = useState(false)
  const [isUploadReportModalOpen, setUploadReportModalOpen] = useState(false)

  const msgKey = 'sent-email-to-the-patient'

  const openNotification = (type: 'error', title: string, description: string) => {
    notificationApi.open({
      type,
      message: title,
      description,
      duration: 0,
      key: 'sent-email-notif',
    })
    setTimeout(() => notificationApi.destroy('sent-email-notif'), 1500)
  }

  useEffect(() => {
    if (isEmailSending) {
      messageApi.open({
        key: msgKey,
        type: 'loading',
        content: t('consultations.email.sending'),
      })
    }

    if (isEmailSentSuccessfully) {
      messageApi.open({ key: msgKey, type: 'success', content: t('consultations.email.sent') })
    }

    if (isEmailSendingHasError) {
      messageApi.destroy(msgKey)
      const error =
        (
          sendEmailError as {
            error?: string
          }
        )?.error ?? `Une erreur est survenue pendant l’envoi de l’e-mail .`
      openNotification('error', t('consultations.email.errorTitle'), error)
    }
    return () => {
      messageApi.destroy(msgKey)
    }
  }, [messageApi, notificationApi, isEmailSendingHasError, isEmailSending, isEmailSentSuccessfully, sendEmailError])

  // Ensure PDF is loaded when needed
  const ensurePdfReportLoaded = async () => {
    if (!pdfReportDocumentId) return undefined
    if (decryptedPdfReportDocument) return decryptedPdfReportDocument

    try {
      setIsLoadingPdf(true)
      const data = await fetchPdfReport(pdfReportDocumentId).unwrap()
      return data
    } catch {
      return undefined
    } finally {
      setIsLoadingPdf(false)
    }
  }

  // Create/revoke object URL only when the blob changes
  const pdfReportUrl = useMemo(() => getPdfUrl(decryptedPdfReportDocument), [decryptedPdfReportDocument])

  useEffect(() => {
    return () => {
      if (pdfReportUrl?.startsWith('blob:')) URL.revokeObjectURL(pdfReportUrl)
    }
  }, [pdfReportUrl])

  const macStatus = useMemo(() => computeDetection(diabeticMaculopathyExaminationValue), [diabeticMaculopathyExaminationValue])
  const retStatus = useMemo(() => computeDetection(diabeticRetinopathyExaminationValue), [diabeticRetinopathyExaminationValue])

  const patientEmail = getTelecomBySystem(patient.addresses, TelecomType.Email)
  const filename = `${t('consultations.reportFilenamePrefix')}_${patient.firstName ?? 'prenom'}_${patient.lastName ?? 'nom'}.pdf`

  // Helper: actually send the email once we have the PDF
  const sendReportEmail = async (pdf: Int8Array) => {
    const emailContent = {
      templateId: SEND_REPORT_TEMPLATE_ID,
      recipient: patientEmail!,
      attachments: [
        {
          content: int8ArrayToBase64(pdf),
          type: 'application/pdf',
          filename,
        },
      ],
      subject: emailSubject,
      html: emailHtml(patient.firstName ?? 'Dear', patient.lastName ?? 'patient'),
    }
    await sendEmail(emailContent).unwrap()
  }

  // Handle "Send by email" click
  const handleSendReportToPatient = async () => {
    if (!patientEmail) return

    // If PDF already available -> send immediately
    if (decryptedPdfReportDocument) {
      await sendReportEmail(decryptedPdfReportDocument)
      return
    }

    // Otherwise, mark intention and load PDF
    setShouldAutoSendEmail(true)
    await ensurePdfReportLoaded()
  }

  // When PDF finishes loading and user wanted to send an email -> send it
  useEffect(() => {
    if (decryptedPdfReportDocument && shouldAutoSendEmail && patientEmail) {
      ;(async () => {
        await sendReportEmail(decryptedPdfReportDocument)
        setShouldAutoSendEmail(false)
      })()
    }
  }, [decryptedPdfReportDocument, shouldAutoSendEmail, patientEmail]) // sendReportEmail is stable enough here

  // Handle "Download" click
  const handleDownloadClick = async (e: React.MouseEvent<HTMLElement>) => {
    if (pdfReportUrl) {
      setShouldAutoDownload(false)
      return
    }

    // No URL yet: prevent default & start loading
    e.preventDefault()
    setShouldAutoDownload(true)
    await ensurePdfReportLoaded()
  }
  const handleUploadTuHubClick = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    await ensurePdfReportLoaded()
    setUploadReportModalOpen(true)
  }

  // When PDF finishes loading and user wanted to download -> trigger download
  useEffect(() => {
    if (pdfReportUrl && shouldAutoDownload) {
      const link = document.createElement('a')
      link.href = pdfReportUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setShouldAutoDownload(false)
    }
  }, [pdfReportUrl, shouldAutoDownload, filename])

  const getHubId = (): HubEnum => (getPropertyStringValue(practitioner?.properties, PRACTITIONER_HUB_ID_IDENTIFIER) as HubEnum | undefined) ?? HubEnum.RSW
  const getFhcUrl = (): string => getPropertyStringValue(practitioner?.properties, PRACTITIONER_FHC_URL_IDENTIFIER) ?? 'https://fhcprd.icure.cloud'
  const getFhcEnvironment = (): 'prod' | 'acc' => (getFhcUrl().includes('fhcprd') ? 'prod' : 'acc')
  const conditionToUploadToHub = () => !!getHubId() && !!getFhcUrl() && !!practitioner?.ssin

  const downloadTooltipKey = !pdfReportDocumentId
    ? 'consultations.download.notAvailable'
    : isFetching || isLoadingPdf
      ? 'consultations.download.preparing'
      : 'consultations.download.tooltip'

  const uploadToHubTooltipKey = !conditionToUploadToHub()
    ? t('consultations.uploadToHub.noHubAvailable')
    : !pdfReportDocumentId
      ? t('consultations.uploadToHub.notAvailable')
      : isFetching || isLoadingPdf
        ? t('consultations.uploadToHub.preparing')
        : t('consultations.uploadToHub.tooltip', { hub: hubsTitles[getHubId()] })

  return (
    <>
      {messageCtx}
      {notificationCtx}
      {isPractitionerFetching && <SpinLoader />}
      <div className="consultationHeader" onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
        <div className="consultationHeader__item">
          <h4>{t('consultations.dateOfExamination')}</h4>
          <p>{dateOfExamination ?? '—'}</p>
        </div>

        <div className="consultationHeader__item">
          <h4>{t('consultations.types.retinopathy')}:</h4>
          <DetectionTag status={retStatus} />
        </div>

        <div className="consultationHeader__item">
          <h4>{t('consultations.types.maculopathy')}:</h4>
          <DetectionTag status={macStatus} />
        </div>

        <div className="consultationHeader__item">
          <h4>{t('consultations.reportTitle')}:</h4>
          <div className="consultationHeader__item__btnsGroup" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={t(downloadTooltipKey)}>
              <Button
                href={pdfReportUrl || undefined}
                download={filename}
                size="small"
                icon={<DownloadOutlined />}
                loading={isFetching || isLoadingPdf}
                disabled={!pdfReportDocumentId}
                rel={pdfReportUrl ? 'noreferrer' : undefined}
                onClick={handleDownloadClick}
              >
                {t('consultations.download.button')}
              </Button>
            </Tooltip>
            <Tooltip title={patientEmail ? t('consultations.email.sendToPatientTooltip') : t('consultations.email.noPatientEmailTooltip')}>
              <Button size="small" icon={<MailOutlined />} disabled={!patientEmail} loading={isEmailSending || isLoadingPdf} onClick={handleSendReportToPatient}>
                {t('consultations.email.sendToPatientButton')}
              </Button>
            </Tooltip>
            <Tooltip title={uploadToHubTooltipKey}>
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
                loading={isFetching || isLoadingPdf}
                disabled={!pdfReportDocumentId || !conditionToUploadToHub()}
                onClick={handleUploadTuHubClick}
              >
                {t('consultations.uploadToHub.button', { hub: hubsTitles[getHubId()] })}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
      {isUploadReportModalOpen && practitioner && decryptedPdfReportDocument && (
        <BeCardinalProvider
          language={i18n.language as SupportedLang}
          onPassphraseVerified={(p) => {
            dispatch(setEhealthPassphrase({ ehealthPassphrase: p }))
          }}
          hubId={getHubId()}
          hcpSsin={practitioner?.ssin ?? ''}
          hcpQuality={extractQualityFromPractitioner(practitioner?.type, practitioner?.codes)}
          transactionTitle={t('consultations.uploadToHub.transactionTitle')}
          transactionCode={'retinoreportia'}
          fhcUrl={getFhcUrl()}
          environment={getFhcEnvironment()}
          passphrase={ehealthPassphrase?.length ? ehealthPassphrase : undefined}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <ModalUploadReportToHub
              isVisible={isUploadReportModalOpen}
              onClose={() => setUploadReportModalOpen(false)}
              patient={patient}
              healthcarePartyId={healthcarePartyId}
              practitionerSsin={practitioner?.ssin}
              pdfReportBlob={new Blob([decryptedPdfReportDocument], { type: 'application/pdf' })}
              selectedHub={hubsTitles[getHubId()]}
            />
          </div>
        </BeCardinalProvider>
      )}
    </>
  )
}
